import { useState } from "react";
import { Box, Button, DialogActions, DialogContent, IconButton, TextField, Typography, CircularProgress } from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CloseIcon from "@mui/icons-material/Close";
import "../../styles/common.css";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { getUserAttributes } from "../../utils/auth";
import ManualBotService from "../../services/manualBot/ManualBotJoinservice";
import { capitalizeFirstLetter, extractDomainFromEmail, getMeetingLinkError } from "../../utils/utils";
import logo7G from "../../assets/sidebar/logo7G Secondary.svg";
import { useQueryClient } from "@tanstack/react-query";

interface JoinMeetingProps {
  setOpen?: (open: boolean) => void;
  defaultMeetingUrl?: string;
  meetingId: string;
  setIsButtonDisabled: (disabled: boolean) => void;
  setTooltipMessage: (message: string) => void;
  isUpcoming?: boolean;
}
const JoinMeeting = ({  setOpen, meetingId, defaultMeetingUrl, setIsButtonDisabled, setTooltipMessage, isUpcoming,  }: JoinMeetingProps) => {
  const { showSnackbar } = useDisplaySnackbar();
  const loggedInEmail = getUserAttributes()?.email;
  const domain = extractDomainFromEmail(loggedInEmail || "");
  const analyzer = capitalizeFirstLetter(domain ?? "");
  const queryClient = useQueryClient();

  const hasNoLink = !defaultMeetingUrl;
  const [meetingUrl, setMeetingUrl] = useState("");
  const [linkError, setLinkError] = useState("");

  const { mutate: postAdhocMeetingBotJoin, isPending: isAddPending } = ManualBotService.useAddAnalyzerAdhoc({
    onSuccess: (result) => {
      if (result.success) {
        showSnackbar("success", result.message || "Analyzer is added successfully!");
      }
      queryClient.invalidateQueries({ queryKey: ["get_interaction_details"] });
      if (setOpen) setOpen(false);
      setIsButtonDisabled(true);
      setTooltipMessage("Button is disabled for 60 seconds to avoid multiple requests");
      setTimeout(() => {
        setIsButtonDisabled(false);
        setTooltipMessage("Add analyzer to the live meeting");
      }, 60000);
    },
    onError: () => {
      showSnackbar("error", "Failed to add analyzer");
    },
  });

  const { mutate: analyzerAction, isPending: isSchedulePending } = ManualBotService.useAnalyzerAction({
    onSuccess: (result) => {
      if (result.success) {
        showSnackbar("success", result.message || "Analyzer is scheduled successfully!");
      }
      queryClient.invalidateQueries({ queryKey: ["get_interaction_details"] });
      if (setOpen) setOpen(false);
      setIsButtonDisabled(true);
      setTooltipMessage("Button is disabled for 60 seconds to avoid multiple requests");
      setTimeout(() => {
        setIsButtonDisabled(false);
        setTooltipMessage("Schedule Analyzer for the meeting");
      }, 60000);
    },
    onError: () => {
      showSnackbar("error", "Failed to schedule analyzer");
    },
  });

  const isPending = isAddPending || isSchedulePending;
  const inputError = hasNoLink ? "" : meetingUrl.trim() ? getMeetingLinkError(meetingUrl.trim()) : "";
  const displayedError = hasNoLink ? linkError : inputError || linkError;

  const isSubmitDisabled =
    isPending ||
    (hasNoLink ? !meetingUrl.trim() : Boolean(inputError));

  const handleSubmit = () => {
    const typedLink = meetingUrl.trim();
    if (hasNoLink) {
      const error = getMeetingLinkError(typedLink);
      if (error) {
        setLinkError(error);
        return;
      }
    } else if (typedLink) {
      const error = getMeetingLinkError(typedLink);
      if (error) {
        setLinkError(error);
        return;
      }
    }

    const finalMeetingUrl = typedLink || "";

    if (isUpcoming) {
      analyzerAction({
        interactionId: meetingId || "",
        analyzerAction: "schedule",
        ...(finalMeetingUrl ? { meetingUrl: finalMeetingUrl } : {}),
      });
    } else {
      postAdhocMeetingBotJoin({
        employeeEmail: loggedInEmail,
        interactionId: meetingId || "",
        ...(finalMeetingUrl ? { meetingUrl: finalMeetingUrl } : {}),
      });
    }
  };

  const handleCloseDialog = () => {
    if (setOpen) setOpen(false);
  };

  return (
    <Box>
      {/* ── Header ── */}
      <Box className="join-meeting-dialog-header">
        <Box className="join-meeting-icon-box">
          <VideocamOutlinedIcon />
        </Box>
        <Box className="join-meeting-header-text">
          <Typography className="join-meeting-header-title">
            {hasNoLink ? "No Meeting Link!" : isUpcoming ? `Schedule ${analyzer}Analyzer for this Interaction` : `Add ${analyzer}Analyzer to this Interaction`}
          </Typography>
          <Typography className="join-meeting-header-subtitle">
            {hasNoLink ? "Add a meeting link to enable Analyzer" : ""}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          className="join-meeting-close-btn"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* ── Content ── */}
      <DialogContent className="join-meeting-content">
        {hasNoLink ? (
          /* No link: single validated textarea */
          <Box className="join-meeting-container">
            <Box className="join-meeting-row">
              <Typography className="join-meeting-label">Add Meeting link</Typography>
              <TextField
                variant="outlined"
                fullWidth
                placeholder="Enter meeting link"
                value={meetingUrl}
                onChange={(e) => {
                  setMeetingUrl(e.target.value);
                  if (linkError) setLinkError("");
                }}
                className="join-meeting-textfield"
                error={Boolean(displayedError)}
                helperText={displayedError}
                autoFocus
              />
            </Box>
          </Box>
        ) : (
          /* Has link: two-field layout */
          <Box className="join-meeting-container">
            <Box className="join-meeting-row">
              <Typography className="join-meeting-label">Scheduled Interaction Link</Typography>
              <TextField
                variant="standard"
                fullWidth
                defaultValue={defaultMeetingUrl}
                InputProps={{
                  readOnly: true,
                  sx: { color: "text.disabled", pointerEvents: "none" },
                }}
              />
            </Box>
            <Box className="join-meeting-row">
              <Typography className="join-meeting-label">New Interaction Link (optional)</Typography>
              <TextField
                label="Enter New Interaction Link"
                variant="outlined"
                fullWidth
                value={meetingUrl}
                onChange={(e) => {
                  setMeetingUrl(e.target.value);
                  if (linkError) setLinkError("");
                }}
                className="join-meeting-textfield"
                error={Boolean(displayedError)}
                helperText={displayedError}
                InputLabelProps={{ sx: { color: "text.disabled" } }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions className="join-meeting-actions">
        <Button
          variant="outlined"
          onClick={handleCloseDialog}
          className="join-meeting-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          className="join-meeting-submit-btn"
          startIcon={
            isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Box
                component="img"
                src={logo7G}
                className="join-meeting-btn-logo"
                alt=""
              />
            )
          }
        >
          {isUpcoming ? "Schedule Analyzer" : "Add Analyzer"}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default JoinMeeting;
